import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { toast } from "react-hot-toast";

const API_BASE = process.env.REACT_APP_API_BASE; // ✅ unified base URL

const WorkerProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);

  // ============================================================
  // 📡 Fetch Worker Data
  // ============================================================
  useEffect(() => {
    const fetchWorker = async () => {
      try {
        const res = await axios.get(`${API_BASE}/admin/worker/${id}`);
        setWorker(res.data);
      } catch (err) {
        console.error("❌ Failed to fetch worker:", err);
        toast.error("Failed to load worker profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchWorker();
  }, [id]);

  // ============================================================
  // 🧾 Update Worker Status
  // ============================================================
  const handleStatusUpdate = async (newStatus) => {
    try {
      await axios.post(`${API_BASE}/admin/update-status/${id}`, {
        status: newStatus,
      });
      setWorker((prev) => ({ ...prev, status: newStatus }));
      toast.success(`✅ Worker ${newStatus} successfully.`);
    } catch (err) {
      console.error("❌ Failed to update status:", err);
      toast.error("Failed to update worker status.");
    }
  };

  // ============================================================
  // 🔗 Generate Full URL for Files
  // ============================================================
  const fullUrl = (url) =>
    url?.startsWith("http") ? url : `${API_BASE.replace("/api", "")}${url}`;

  if (loading)
    return (
      <div className="p-10 text-gray-600 animate-pulse">Loading profile...</div>
    );
  if (!worker)
    return (
      <div className="p-10 text-red-600 font-semibold">
        ❌ Worker not found.
      </div>
    );

  // ============================================================
  // 🖥️ Render
  // ============================================================
  return (
    <div className="flex font-[Poppins]">
      <Sidebar />
      <main className="ml-0 md:ml-64 p-8 w-full bg-gray-100 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-extrabold text-gray-800">
            🧰 Worker Profile:{" "}
            <span className="text-indigo-600">{worker.name}</span>
          </h1>
          <div className="space-x-2">
            <button
              onClick={() => handleStatusUpdate("approved")}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-semibold"
            >
              Approve
            </button>
            <button
              onClick={() => handleStatusUpdate("rejected")}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 font-semibold"
            >
              Reject
            </button>
          </div>
        </div>

        {/* ================= Personal Info ================= */}
        <section className="bg-white p-6 rounded-xl shadow mb-8">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">
            👤 Personal Info
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-3 text-gray-800 text-sm">
            <p>
              <strong>Email:</strong> {worker.email}
            </p>
            <p>
              <strong>Phone:</strong> {worker.phone}
            </p>
            <p>
              <strong>Address:</strong> {worker.address}
            </p>
            <p>
              <strong>City:</strong> {worker.city}
            </p>
            <p>
              <strong>Province:</strong> {worker.province}
            </p>
            <p>
              <strong>Postal Code:</strong> {worker.postalCode}
            </p>
            <p>
              <strong>Status:</strong>{" "}
              <span
                className={`font-semibold ${
                  worker.status === "approved"
                    ? "text-green-600"
                    : worker.status === "pending"
                    ? "text-yellow-600"
                    : "text-red-600"
                }`}
              >
                {worker.status}
              </span>
            </p>
            <p>
              <strong>Tier:</strong> {worker.tier || "N/A"}
            </p>
          </div>
        </section>

        {/* ================= Uploaded Documents ================= */}
        <section className="bg-white p-6 rounded-xl shadow mb-8">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">
            📄 Uploaded Documents
          </h2>
          <ul className="text-sm text-gray-800 space-y-2">
            {worker.id1Url && (
              <li>
                <strong>{worker.id1Type}:</strong>{" "}
                <a
                  href={fullUrl(worker.id1Url)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline"
                >
                  View
                </a>
              </li>
            )}
            {worker.id2Url && (
              <li>
                <strong>{worker.id2Type}:</strong>{" "}
                <a
                  href={fullUrl(worker.id2Url)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline"
                >
                  View
                </a>
              </li>
            )}
            {worker.isInternational && worker.permitUrl && (
              <li>
                <strong>Work/Study Permit:</strong>{" "}
                <a
                  href={fullUrl(worker.permitUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline"
                >
                  View
                </a>
              </li>
            )}
          </ul>
        </section>

        {/* ================= Services & Certifications ================= */}
        <section className="bg-white p-6 rounded-xl shadow mb-8">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">
            🧾 Services & Certifications
          </h2>
          {worker.services?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {worker.services.map((s, i) => (
                <div
                  key={i}
                  className="bg-gray-50 border rounded-lg p-4 text-sm shadow-sm"
                >
                  <p>
                    <strong>Service:</strong> {s.name}
                  </p>
                  <p>
                    <strong>Has Tools:</strong> {s.hasTools ? "Yes" : "No"}
                  </p>
                  {s.name === "Moving Help" && (
                    <p>
                      <strong>Has Truck:</strong> {s.hasTruck ? "Yes" : "No"}
                    </p>
                  )}
                  <p>
                    <strong>Certificate:</strong>{" "}
                    {s.certUrl ? (
                      <a
                        href={fullUrl(s.certUrl)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 underline"
                      >
                        View
                      </a>
                    ) : (
                      "Not Uploaded"
                    )}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No services listed.</p>
          )}
        </section>

        {/* ================= Assigned Jobs ================= */}
        <section className="bg-white p-6 rounded-xl shadow">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">
            📦 Assigned Jobs
          </h2>
          {worker.jobsAssigned?.length > 0 ? (
            <ul className="text-sm text-gray-700 space-y-2 list-disc pl-5">
              {worker.jobsAssigned.map((jobId, idx) => (
                <li key={idx}>Job ID: {jobId}</li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">No jobs assigned yet.</p>
          )}
        </section>
      </main>
    </div>
  );
};

export default WorkerProfilePage;
